import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Provider } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { store } from "./store/Store.js";
import "bootstrap/dist/css/bootstrap.min.css";
import Login from "./components/auth/Login";
import Dashboard from "./components/dashboard/Dashboard";
import Layout from "./components/common/Layout";
import ProtectedRoute from "./components/common/ProtectedRoute";
import { useAppSelector } from "./store/hooks";
import { FEATURES, isFeatureBlocked } from "./config/roleFeatures";
import THEME from "./utils/themeConfig";
import Products from "./components/products/Products";
import Vendors from "./components/vendors/Vendors";
import Customers from "./components/customers/Customers";
import Category from "./components/category/Category";
import Brands from "./components/brands/Brands";
import Batches from "./components/batches/Batches";
import Purchases from "./components/purchases/Purchases";
import Inventory from "./components/inventory/Inventory";
import Orders from "./components/orders/Orders";
import OrderView from "./components/orders/OrderView";
import OrderViewPrint from "./components/orders/components/OrderViewPrint";
import ManageStore from "./components/manageStore/ManageStore";
import Settings from "./components/settings/Settings";
import VendorDetails from "./components/vendors/VendorDetails";
import ProductDetails from "./components/products/ProductDetails";
import CustomerDetails from "./components/customers/CustomerDetails";
import CustomerPrint from "./components/customers/CustomerPrint";
import CustomerDetailsPrint from "./components/customers/CustomerDetailsPrint";
import "./App.css";
import "./design-system.css";
import POS from "./components/features/pos/Pos";
import Finance from "./components/finance/index";
import Journal from "./components/finance/Journal/Journal";
import DailyReportPrint from "./components/finance/Journal/components/DailyReportPrint";
import Reports from "./components/finance/Reports/Reports";
import DueCustomerPrint from "./components/finance/Reports/DueCustomerPrint";
import CustomersOverviewPrint from "./components/finance/Reports/CustomersOverviewPrint";
import Sales from "./components/sales/Sales";
import ViewSales from "./components/sales/ViewSales";
import PrintInvoice from "./components/sales/components/PrintInvoice";
import "./style/sales.css";
import IMEIList from "./components/imei/IMEIList";
import IMEIRecordDetail from "./components/imei/IMEIRecordDetail";
import RegisterReturns from "./components/returns/register";
import RepairReserve from "./components/returns/repair-reserve";
import VendorReturns from "./components/returns/vendor-returns";
import VendorReturnDetail from "./components/returns/vendor-return-detail";
import LowStock from "./components/low-stock";
import EmployeeManagement from "./components/employee-management/EmployeeManagement";
import TransferHistory from "./components/transfer-history/TransferHistory";
import TransferHistoryDetail from "./components/transfer-history/TransferHistoryDetail";
import TransferToPos from "./components/transfer_to_pos/TransferToPos";
import TransferDetail from "./components/transfer_to_pos/transferDetail";
import ProductTags from "./components/ProductTags/ProductTags.js";
import TagDetail from "./components/ProductTags/TagDetail.js";


// Component to handle header navigation
const AppWithNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const { title } = useAppSelector((state) => state.domain);

  // Set document title based on domain
  React.useEffect(() => {
    document.title = title;
  }, [title]);

  // Redirect to login if not authenticated and not already on login page
  React.useEffect(() => {
    if (!isAuthenticated && location.pathname !== "/login") {
      navigate("/login", { replace: true });
    }
    
    // Redirect away from blocked features using roleFeatures configuration
    if (isFeatureBlocked(FEATURES.DASHBOARD) && location.pathname === "/dashboard") {
      navigate("/products", { replace: true });
    }
    if (isFeatureBlocked(FEATURES.POS) && location.pathname === "/pos") {
      navigate("/products", { replace: true });
    }
    if (isFeatureBlocked(FEATURES.ORDERS) && location.pathname.startsWith("/orders")) {
      navigate("/products", { replace: true });
    }
    if (isFeatureBlocked(FEATURES.SALES) && location.pathname.startsWith("/sales")) {
      navigate("/products", { replace: true });
    }
    if (isFeatureBlocked(FEATURES.FINANCE) && location.pathname.startsWith("/finance")) {
      navigate("/products", { replace: true });
    }
    if (isFeatureBlocked(FEATURES.CUSTOMERS) && (location.pathname === "/customers" || location.pathname.startsWith("/customer/"))) {
      navigate("/products", { replace: true });
    }
    if (isFeatureBlocked(FEATURES.VENDORS) && (location.pathname === "/vendors" || location.pathname.startsWith("/vendor/"))) {
      navigate("/products", { replace: true });
    }
    if (isFeatureBlocked(FEATURES.RETURNS) && location.pathname.startsWith("/returns")) {
      navigate("/products", { replace: true });
    }
    if (isFeatureBlocked(FEATURES.BRANDS) && location.pathname === "/brands") {
      navigate("/products", { replace: true });
    }
    if (isFeatureBlocked(FEATURES.CATEGORIES) && location.pathname === "/category") {
      navigate("/products", { replace: true });
    }
    if (isFeatureBlocked(FEATURES.BATCHES) && location.pathname === "/batches") {
      navigate("/products", { replace: true });
    }
    if (isFeatureBlocked(FEATURES.PURCHASES) && location.pathname === "/purchases") {
      navigate("/products", { replace: true });
    }
  }, [isAuthenticated, location.pathname, navigate]);

  const handleHeaderNavigation = (value) => {
    const currentPath = location.pathname;

    // Handle finance navigation
    if (currentPath.startsWith("/finance")) {
      if (value === "journal") {
        navigate("/finance/journal");
      } else if (value === "reports") {
        navigate("/finance/reports");
      }
    }
    // Handle products navigation
    else if (
      currentPath.startsWith("/products") ||
      currentPath.startsWith("/transfer-history") ||
      currentPath === "/brands" ||
      currentPath === "/category" ||
      currentPath === "/batches" ||
      currentPath.startsWith("/batches/") ||
      currentPath === "/purchases" ||
      currentPath === "/low-stock" ||
      currentPath === "/tags" ||
      currentPath.startsWith("/tags/") ||
      currentPath.startsWith("/product/")
    ) {
      if (value === "products") {
        navigate("/products");
      } else if (value === "transfer-history") {
        navigate("/transfer-history");
      } else if (value === "brands") {
        navigate("/brands");
      } else if (value === "categories") {
        navigate("/category");
      } else if (value === "batches") {
        navigate("/batches");
      } else if (value === "purchases") {
        navigate("/purchases");
      } else if (value === "low-stock") {
        navigate("/low-stock");
      } else if (value === "tags") {
        navigate("/tags");
      }
    }
    // Handle returns navigation
    else if (currentPath.startsWith("/returns")) {
      if (value === "register") {
        navigate("/returns/register");
      } else if (value === "repair-reserve") {
        navigate("/returns/repair-reserve");
      } else if (value === "vendor-returns") {
        navigate("/returns/vendor");
      }
    }
  };

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiredFeature={FEATURES.DASHBOARD}>
            <Layout onHeaderNavigation={handleHeaderNavigation}>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <Inventory />
          </Layout>
        }
      />
      <Route
        path="/products"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <Products />
          </Layout>
        }
      />
      <Route
        path="/transfer_to_pos"
        element={
          <ProtectedRoute requiredFeature={FEATURES.TRANSFER_TO_POS}>
            <Layout onHeaderNavigation={handleHeaderNavigation}>
              <TransferToPos />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/transfer_to_pos/:id"
        element={
          <ProtectedRoute requiredFeature={FEATURES.TRANSFER_TO_POS}>
            <Layout onHeaderNavigation={handleHeaderNavigation}>
              <TransferDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/batches"
        element={
          <ProtectedRoute requiredFeature={FEATURES.BATCHES}>
            <Layout onHeaderNavigation={handleHeaderNavigation}>
              <Batches />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <div>General Reports</div>
          </Layout>
        }
      />
      <Route
        path="/vendors"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <Vendors />
          </Layout>
        }
      />
      <Route
        path="/customers"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <Customers />
          </Layout>
        }
      />
      <Route
        path="/pos"
        element={
          <ProtectedRoute requiredFeature={FEATURES.POS}>
            <Layout onHeaderNavigation={handleHeaderNavigation}>
              <POS />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/print/:id"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <PrintInvoice />
          </Layout>
        }
      />
      <Route
        path="/sales/view/:id"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <ViewSales />
          </Layout>
        }
      />
      <Route
        path="/sales"
        element={
          <ProtectedRoute requiredFeature={FEATURES.SALES}>
            <Layout onHeaderNavigation={handleHeaderNavigation}>
              <Sales />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <Finance />
          </Layout>
        }
      />
      <Route
        path="/finance/journal"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <Journal />
          </Layout>
        }
      />
      <Route
        path="/finance/journal/daily-report/print"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <DailyReportPrint />
          </Layout>
        }
      />
      <Route
        path="/finance/reports"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <Reports />
          </Layout>
        }
      />
      <Route
        path="/finance/reports/due-customers/print"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <DueCustomerPrint />
          </Layout>
        }
      />
      <Route
        path="/finance/reports/customers-overview/print"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <CustomersOverviewPrint />
          </Layout>
        }
      />
      <Route
        path="/imei"
        element={
          <ProtectedRoute requiredFeature={FEATURES.IMEI}>
            <Layout onHeaderNavigation={handleHeaderNavigation}>
              <IMEIList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/imei/:id"
        element={
          <ProtectedRoute requiredFeature={FEATURES.IMEI}>
            <Layout onHeaderNavigation={handleHeaderNavigation}>
              <IMEIRecordDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute requiredFeature={FEATURES.ORDERS}>
            <Layout onHeaderNavigation={handleHeaderNavigation}>
              <Orders />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:id"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <OrderView />
          </Layout>
        }
      />
      <Route
        path="/orders/:id/print"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <OrderViewPrint />
          </Layout>
        }
      />
      <Route
        path="/tags"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <ProductTags />
          </Layout>
        }
      />
      <Route
        path="/tags/:id"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <TagDetail />
          </Layout>
        }
      />
      <Route
        path="/category"
        element={
          <ProtectedRoute requiredFeature={FEATURES.CATEGORIES}>
            <Layout onHeaderNavigation={handleHeaderNavigation}>
              <Category />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manageStore"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <ManageStore />
          </Layout>
        }
      />
      <Route
        path="/brands"
        element={
          <ProtectedRoute requiredFeature={FEATURES.BRANDS}>
            <Layout onHeaderNavigation={handleHeaderNavigation}>
              <Brands />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchases"
        element={
          <ProtectedRoute requiredFeature={FEATURES.PURCHASES}>
            <Layout onHeaderNavigation={handleHeaderNavigation}>
              <Purchases />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/low-stock"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <LowStock />
          </Layout>
        }
      />
      <Route
        path="/transfer-history"
        element={
          <ProtectedRoute requiredFeature={FEATURES.TRANSFER_HISTORY}>
            <Layout onHeaderNavigation={handleHeaderNavigation}>
              <TransferHistory />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/transfer-history/:id"
        element={
          <ProtectedRoute requiredFeature={FEATURES.TRANSFER_HISTORY}>
            <Layout onHeaderNavigation={handleHeaderNavigation}>
              <TransferHistoryDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <Settings />
          </Layout>
        }
      />
      <Route
        path="/vendor/:id"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <VendorDetails />
          </Layout>
        }
      />
      <Route
        path="/product/:id"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <ProductDetails />
          </Layout>
        }
      />
      <Route
        path="/customer/:id"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <CustomerDetails />
          </Layout>
        }
      />
      <Route
        path="/customers/print"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <CustomerPrint />
          </Layout>
        }
      />
      <Route
        path="/customer/:id/print"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <CustomerDetailsPrint />
          </Layout>
        }
      />

      {!isFeatureBlocked(FEATURES.RETURNS) && (
        <>
          <Route
            path="/returns"
            element={<Navigate to="/returns/register" replace />}
          />
          <Route
            path="/returns/register"
            element={
              <ProtectedRoute requiredFeature={FEATURES.RETURNS}>
                <Layout onHeaderNavigation={handleHeaderNavigation}>
                  <RegisterReturns />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/returns/repair-reserve"
            element={
              <ProtectedRoute requiredFeature={FEATURES.RETURNS}>
                <Layout onHeaderNavigation={handleHeaderNavigation}>
                  <RepairReserve />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/returns/vendor"
            element={
              <ProtectedRoute requiredFeature={FEATURES.RETURNS}>
                <Layout onHeaderNavigation={handleHeaderNavigation}>
                  <VendorReturns />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/returns/vendor/:id"
            element={
              <ProtectedRoute requiredFeature={FEATURES.RETURNS}>
                <Layout onHeaderNavigation={handleHeaderNavigation}>
                  <VendorReturnDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
        </>
      )}
      <Route
        path="/employee-management"
        element={
          <Layout onHeaderNavigation={handleHeaderNavigation}>
            <EmployeeManagement />
          </Layout>
        }
      />

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

function App() {
  React.useEffect(() => {
    Object.entries(THEME).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
  }, []);

  return (
    <Provider store={store}>
      <Router>
        <div className="App">
          <AppWithNavigation />
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </div>
      </Router>
    </Provider>
  );
}

export default App;
